import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from './AuthContext';
import '../styles/Login.css';

const Login = ({ lang }) => {
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login, verify2fa, sendChallengeEmail } = useAuth();
  const navigate = useNavigate();

  // ── 2FA challenge state ──
  // After password is accepted but 2FA is required, the server returns a
  // challenge instead of a token. We hold it here and render a second
  // step instead of navigating away.
  const [challenge, setChallenge] = useState(null); // { challengeId, methods, emailHint }
  const [method, setMethod] = useState('');         // 'totp' | 'email'
  const [code, setCode] = useState('');
  const [emailCooldown, setEmailCooldown] = useState(0);

  React.useEffect(() => {
    if (!emailCooldown) return;
    const t = setInterval(() => setEmailCooldown(c => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [emailCooldown]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await login(formData.username.trim(), formData.password.trim());
      if (data && data.twofa) {
        setChallenge(data);
        // Default to TOTP if available; for email-only, auto-trigger send.
        const initial = data.methods.includes('totp') ? 'totp' : 'email';
        setMethod(initial);
        if (initial === 'email') {
          try { await sendChallengeEmail(data.challengeId); setEmailCooldown(15); }
          catch (err) { setError(err.message); }
        }
        return;
      }
      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await verify2fa({ challengeId: challenge.challengeId, method, code: code.trim() });
      navigate('/dashboard');
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const switchMethod = async (m) => {
    setMethod(m); setCode(''); setError('');
    if (m === 'email' && emailCooldown === 0) {
      try { await sendChallengeEmail(challenge.challengeId); setEmailCooldown(15); }
      catch (err) { setError(err.message); }
    }
  };

  const resendEmail = async () => {
    if (emailCooldown > 0) return;
    setError('');
    try { await sendChallengeEmail(challenge.challengeId); setEmailCooldown(15); }
    catch (err) { setError(err.message); }
  };

  return (
    <section className="login-page">
      <div className="container">
        <motion.div
          className="login-wrapper"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="login-left">
            <div className="login-brand">
              <span className="login-terminal-line">
                {lang.LOGIN_TERMINAL || 'root@ispclub:~/# ./ispclub --login'}
              </span>
              <h1 className="login-title">{lang.LOGIN_TITLE || 'Member Login'}</h1>
              <p className="login-desc">
                {lang.LOGIN_DESC || 'Sign in to access your member dashboard, resources, and tools.'}
              </p>
            </div>

            <div className="login-features">
              <div className="login-feature">
                <div className="feature-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  </svg>
                </div>
                <div>
                  <h4>{lang.LOGIN_FEATURE_1_TITLE || 'Secure Access'}</h4>
                  <p>{lang.LOGIN_FEATURE_1_DESC || 'Protected member-only resources and tools'}</p>
                </div>
              </div>
              <div className="login-feature">
                <div className="feature-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                </div>
                <div>
                  <h4>{lang.LOGIN_FEATURE_2_TITLE || 'Community'}</h4>
                  <p>{lang.LOGIN_FEATURE_2_DESC || 'Connect with fellow cybersecurity enthusiasts'}</p>
                </div>
              </div>
              <div className="login-feature">
                <div className="feature-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                    <line x1="8" y1="21" x2="16" y2="21"/>
                    <line x1="12" y1="17" x2="12" y2="21"/>
                  </svg>
                </div>
                <div>
                  <h4>{lang.LOGIN_FEATURE_3_TITLE || 'Resources'}</h4>
                  <p>{lang.LOGIN_FEATURE_3_DESC || 'Training materials, writeups, and CTF tools'}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="login-right">
            {!challenge ? (
            <form className="login-form" onSubmit={handleSubmit}>
              {error && <div className="login-error">{error}</div>}

              <div className="form-group">
                <label htmlFor="username">{lang.LOGIN_FORM_USERNAME || 'Username or Email'}</label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  required
                  placeholder={lang.LOGIN_FORM_USERNAME_PLACEHOLDER || 'Enter username or email'}
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">{lang.LOGIN_FORM_PASSWORD || 'Password'}</label>
                <div className="login-password-wrap">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    placeholder={lang.LOGIN_FORM_PASSWORD_PLACEHOLDER || 'Enter password'}
                  />
                  <button
                    type="button"
                    className="login-password-toggle"
                    onClick={() => setShowPassword(v => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div className="login-forgot-row">
                <Link to="/forgot-password" className="login-forgot-link">
                  {lang.LOGIN_FORGOT_PASSWORD || 'Forgot password?'}
                </Link>
              </div>

              <button
                type="submit"
                className="btn btn-primary login-submit"
                disabled={loading}
              >
                {loading ? (lang.LOGIN_FORM_LOADING || 'Signing in...') : (lang.LOGIN_FORM_SUBMIT || 'Sign In')}
              </button>
            </form>
            ) : (
            <form className="login-form" onSubmit={handleVerify}>
              {error && <div className="login-error">{error}</div>}
              <h3 style={{ margin: '0 0 12px' }}> Xác thực hai lớp</h3>
              <p className="form-hint" style={{ marginBottom: 14 }}>
                Vui lòng nhập mã 6 chữ số để hoàn tất đăng nhập.
              </p>

              {challenge.methods.length > 1 && (
                <div className="form-group" style={{ display: 'flex', gap: 8 }}>
                  {challenge.methods.includes('totp') && (
                    <button type="button" className={`btn btn-sm ${method === 'totp' ? 'btn-primary' : 'btn-outline'}`} onClick={() => switchMethod('totp')}>
                       App xác thực
                    </button>
                  )}
                  {challenge.methods.includes('email') && (
                    <button type="button" className={`btn btn-sm ${method === 'email' ? 'btn-primary' : 'btn-outline'}`} onClick={() => switchMethod('email')}>
                       Email
                    </button>
                  )}
                </div>
              )}

              {method === 'email' && challenge.emailHint && (
                <p className="form-hint" style={{ marginBottom: 10 }}>
                  Đã gửi mã đến <strong>{challenge.emailHint}</strong> (hiệu lực 5 phút).
                </p>
              )}

              <div className="form-group">
                <label htmlFor="twofa-code">Mã xác thực</label>
                <input
                  type="text"
                  id="twofa-code"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  value={code}
                  onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  style={{ fontFamily: 'monospace', fontSize: '1.15rem', letterSpacing: 6, textAlign: 'center' }}
                  autoFocus
                  required
                />
              </div>

              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <button type="submit" className="btn btn-primary" disabled={loading || code.length !== 6}>
                  {loading ? 'Đang xác thực...' : 'Xác nhận'}
                </button>
                {method === 'email' && (
                  <button type="button" className="btn btn-outline btn-sm" onClick={resendEmail} disabled={emailCooldown > 0}>
                    {emailCooldown > 0 ? `Gửi lại (${emailCooldown}s)` : 'Gửi lại mã'}
                  </button>
                )}
                <button type="button" className="btn btn-outline btn-sm" onClick={() => { setChallenge(null); setCode(''); setError(''); }}>
                  Huỷ
                </button>
              </div>
            </form>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Login;
